import { useIntl } from 'react-intl';

import { ColumnVisibility } from '@/components/common/ColumnVisibility/ColumnVisibility';
import { useColumnVisibility } from '@/components/common/ColumnVisibility/useColumnVisibility';
import SectionHeader from '@/components/doohReport/common/SectionHeader/SectionHeader';
import TopScreensList from './TopScreensList/TopScreensList';
import {
  getTopScreensColumnLabels,
  TOP_SCREENS_DEFAULT_HIDDEN_COLUMN_IDS,
  TOP_SCREENS_HIDEABLE_COLUMN_IDS,
  TOP_SCREENS_LIST_ID
} from './TopScreensList/topScreensColumns';

import type { DoohReportScreen } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './deliveryDrivers.scss';

type Props = {
  screens: DoohReportScreen[];
  sectionNumber: string;
};

/** Columns "Reset to default" leaves visible in the "Columns" menu. */
const DEFAULT_VISIBLE_COLUMN_IDS = [
  'zone',
  ...TOP_SCREENS_HIDEABLE_COLUMN_IDS.filter(id => !TOP_SCREENS_DEFAULT_HIDDEN_COLUMN_IDS.includes(id))
];

/**
 * "Delivery Drivers" section of the DOOH report: the ranked table of the
 * best-performing screens in the circuit, with a "Columns" menu to pick its columns.
 */
export default function DeliveryDrivers({ screens, sectionNumber }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { applyVisibleColumns, isVisible } = useColumnVisibility(
    TOP_SCREENS_LIST_ID,
    TOP_SCREENS_HIDEABLE_COLUMN_IDS,
    TOP_SCREENS_DEFAULT_HIDDEN_COLUMN_IDS
  );
  const labels = getTopScreensColumnLabels(formatMessage);
  const columnOptions = [
    { disabled: true, id: 'zone', label: labels.zone, visible: true },
    ...TOP_SCREENS_HIDEABLE_COLUMN_IDS.map(id => ({
      id,
      label: labels[id as keyof typeof labels],
      visible: isVisible(id)
    }))
  ];

  return (
    <section className="deliveryDrivers">
      <SectionHeader
        description={formatMessage({
          defaultMessage: 'Why these screens worked.',
          id: 'doohReport.deliveryDrivers.description'
        })}
        number={sectionNumber}
        title={formatMessage({ defaultMessage: 'Delivery Drivers.', id: 'doohReport.deliveryDrivers.title' })}
        tooltip={formatMessage({
          defaultMessage:
            'The geo-context behind delivery — what surrounds the screens that concentrated the most on target impact.',
          id: 'doohReport.deliveryDrivers.tooltip'
        })}
      />
      <div className="deliveryDrivers__card">
        <div className="deliveryDrivers__cardHeader">
          <span className="deliveryDrivers__cardTitle">
            <i className="fa-solid fa-star deliveryDrivers__cardTitleIcon" />
            {formatMessage({
              defaultMessage: 'Top screens by on-target impact',
              id: 'doohReport.deliveryDrivers.cardTitle'
            })}
          </span>
          <ColumnVisibility
            columns={columnOptions}
            defaultVisibleColumnIds={DEFAULT_VISIBLE_COLUMN_IDS}
            label={formatMessage({ defaultMessage: 'Columns', id: 'doohReport.deliveryDrivers.columns' })}
            onApply={applyVisibleColumns}
            prefixId="deliveryDrivers"
            size="ultraSmall"
          />
        </div>
        <TopScreensList isColumnVisible={isVisible} screens={screens} />
      </div>
    </section>
  );
}
